/**
 * useAuthContext Hook
 * RBAC 시스템용 AuthContext를 제공하는 Hook
 *
 * Firebase가 초기화되지 않은 경우 DEMO 모드로 작동 (super_admin 권한)
 */

import { useState, useEffect } from 'react';
import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  Firestore,
} from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { AuthContext } from '../../types/rbac';
import { RoleAssignment } from '../../types/roleAssignment';
import { PermissionKey } from '../../types/permission';
import { demoUserContext_superAdmin } from '../../utils/demoAuthContexts';
import { AssignmentTargetType } from '../../types/auth';

/**
 * 현재 사용자의 AuthContext를 반환
 *
 * **DEMO 모드:**
 * Firebase가 초기화되지 않았거나 연결되지 않은 경우,
 * super_admin 권한을 가진 DEMO 사용자를 반환합니다.
 *
 * **실제 모드:**
 * Firebase에서 사용자의 역할 할당 정보를 실시간으로 구독합니다.
 *
 * @returns {AuthContext} AuthContext 객체
 */
export function useAuthContext(): AuthContext {
  const [authContext, setAuthContext] = useState<AuthContext>({
    userId: '',
    roleAssignments: [],
    isLoaded: false,
  });

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우 DEMO 모드
    if (!auth || !db) {
      setAuthContext(demoUserContext_superAdmin);
      return () => {};
    }

    // Firebase Auth 상태 변화 구독
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        try {
          // 로그아웃 상태
          if (!firebaseUser) {
            setAuthContext({
              userId: '',
              roleAssignments: [],
              isLoaded: true,
            });
            return;
          }

          const uid = firebaseUser.uid;

          // V166: 사용자 프로필 조회 (positionId, teamId, orgUnitId 필요)
          const userDocRef = doc(db!, 'users', uid);
          const userDoc = await getDoc(userDocRef);
          const userData = userDoc.exists() ? userDoc.data() : null;

          // V166: 모든 targetType에 대해 역할 할당 조회
          // (useCurrentUser.ts의 fetchUserRoleAssignments와 동일한 로직)
          const roleAssignments = await fetchAllRoleAssignments(
            db!,
            uid,
            userData?.positionId,
            userData?.teamId,
            userData?.orgUnitId
          );

          const roleIds = roleAssignments.map(ra => ra.roleId);
          // V167: auth_roles에서 permissions 조회
          const permissions = await fetchPermissionsFromRoles(db!, roleIds);

          // 사용자 프로필에서 orgUnitId 가져오기
          const orgUnitId = userData?.orgUnitId || roleAssignments.find(ra => ra.orgUnitId)?.orgUnitId;

          setAuthContext({
            userId: uid,
            roleAssignments,
            permissions,
            orgUnitId,
            currentEmploymentTypeCode: userData?.currentEmploymentTypeCode, // V257
            isLoaded: true,
          });
        } catch (err) {
          console.error('AuthContext 로드 오류:', err);
          setAuthContext({
            userId: '',
            roleAssignments: [],
            isLoaded: true,
          });
        }
      },
      (err) => {
        console.error('Auth 구독 오류:', err);
        setAuthContext({
          userId: '',
          roleAssignments: [],
          isLoaded: true,
        });
      }
    );

    return () => unsubscribe();
  }, []);

  return authContext;
}

/**
 * ============================================
 * TODO: Firestore 연동 완성 (향후 구현)
 * ============================================
 *
 * 현재는 roleAssignments만 조회하고 있지만,
 * 향후 users 컬렉션에서 orgUnitId도 함께 조회해야 합니다.
 *
 * **구현 방법:**
 *
 * ```tsx
 * // 사용자 프로필 조회
 * const userDocRef = doc(db!, 'users', uid);
 * const userDoc = await getDoc(userDocRef);
 * const userData = userDoc.data();
 * const orgUnitId = userData?.orgUnitId;
 *
 * setAuthContext({
 *   userId: uid,
 *   roleAssignments,
 *   orgUnitId,  // users 문서에서 가져온 orgUnitId
 *   isLoaded: true,
 * });
 * ```
 */

/**
 * ============================================
 * DEMO 모드 사용 가이드
 * ============================================
 *
 * Firebase가 초기화되지 않은 경우, 자동으로 DEMO 모드로 전환됩니다.
 * DEMO 모드에서는 super_admin 권한을 가진 사용자로 작동합니다.
 *
 * **다른 역할로 테스트하려면:**
 *
 * 1. demoAuthContexts.ts에서 원하는 역할 선택:
 *    - DEMO_AUTH_CONTEXTS.general (일반 직원)
 *    - DEMO_AUTH_CONTEXTS.hr (인사관리자)
 *    - DEMO_AUTH_CONTEXTS.orgManager (조직장)
 *    - 등등...
 *
 * 2. 이 파일에서 import 수정:
 *    ```tsx
 *    import { demoUserContext_general } from '../../utils/demoAuthContexts';
 *    setAuthContext(demoUserContext_general);
 *    ```
 *
 * 3. 또는 환경변수로 DEMO 역할 선택 가능하도록 구현:
 *    ```tsx
 *    const DEMO_ROLE = process.env.REACT_APP_DEMO_ROLE || 'super_admin';
 *    const demoContext = DEMO_AUTH_CONTEXTS[DEMO_ROLE];
 *    setAuthContext(demoContext);
 *    ```
 */

/**
 * V166: 사용자의 모든 역할 할당 조회
 * (개인 할당 + 직급 할당 + 팀 할당 + 조직 할당)
 *
 * useCurrentUser.ts의 fetchUserRoleAssignments와 동일한 로직
 * 중복 방지를 위해 roleId 기준으로 Set 관리
 */
async function fetchAllRoleAssignments(
  db: Firestore,
  userId: string,
  positionId?: string,
  teamId?: string,
  orgUnitId?: string
): Promise<RoleAssignment[]> {
  const assignments: RoleAssignment[] = [];
  const addedRoleIds = new Set<string>(); // 중복 방지

  const addAssignments = (snapshot: any) => {
    snapshot.docs.forEach((docSnap: any) => {
      const data = docSnap.data();
      if (!addedRoleIds.has(data.roleId)) {
        addedRoleIds.add(data.roleId);
        assignments.push({
          id: docSnap.id,
          userId: data.targetId || userId,
          roleId: data.roleId,
          orgUnitId: data.orgUnitId,
          isActive: data.status === 'active',
          createdAt: data.createdAt,
          createdBy: data.createdBy,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy,
        } as RoleAssignment);
      }
    });
  };

  try {
    // 1. 개인 할당 (targetType: 'user')
    const userAssignmentsQuery = query(
      collection(db, 'auth_roleAssignments'),
      where('targetType', '==', AssignmentTargetType.USER),
      where('targetId', '==', userId),
      where('status', '==', 'active')
    );
    const userSnapshot = await getDocs(userAssignmentsQuery);
    addAssignments(userSnapshot);

    // 2. 직급 할당 (targetType: 'position')
    if (positionId) {
      const positionAssignmentsQuery = query(
        collection(db, 'auth_roleAssignments'),
        where('targetType', '==', AssignmentTargetType.POSITION),
        where('targetId', '==', positionId),
        where('status', '==', 'active')
      );
      const positionSnapshot = await getDocs(positionAssignmentsQuery);
      addAssignments(positionSnapshot);
    }

    // 3. 팀 할당 (targetType: 'team')
    if (teamId) {
      const teamAssignmentsQuery = query(
        collection(db, 'auth_roleAssignments'),
        where('targetType', '==', AssignmentTargetType.TEAM),
        where('targetId', '==', teamId),
        where('status', '==', 'active')
      );
      const teamSnapshot = await getDocs(teamAssignmentsQuery);
      addAssignments(teamSnapshot);
    }

    // 4. 조직 할당 (targetType: 'orgUnit')
    if (orgUnitId) {
      const orgAssignmentsQuery = query(
        collection(db, 'auth_roleAssignments'),
        where('targetType', '==', AssignmentTargetType.ORG_UNIT),
        where('targetId', '==', orgUnitId),
        where('status', '==', 'active')
      );
      const orgSnapshot = await getDocs(orgAssignmentsQuery);
      addAssignments(orgSnapshot);
    }
  } catch (err) {
    console.error('[V166] auth_roleAssignments 조회 실패:', err);
  }

  return assignments;
}

/**
 * V167: 역할 ID 목록으로 auth_roles 컬렉션에서 permissions 조회
 *
 * DB 기반 권한 SSoT:
 * - 프론트엔드 하드코딩(ROLE_PERMISSIONS) 대신 auth_roles.permissions 사용
 * - 역할 권한 수정 시 프론트 배포 불필요
 *
 * @param db Firestore 인스턴스
 * @param roleIds 조회할 역할 ID 목록
 * @returns 중복 제거된 권한 문자열 배열
 */
async function fetchPermissionsFromRoles(
  db: Firestore,
  roleIds: string[]
): Promise<PermissionKey[]> {
  if (roleIds.length === 0) {
    return [];
  }

  const allPermissions: string[] = [];

  try {
    // Firestore 'in' 쿼리는 최대 10개까지만 지원
    // 10개씩 나눠서 조회
    const chunks: string[][] = [];
    for (let i = 0; i < roleIds.length; i += 10) {
      chunks.push(roleIds.slice(i, i + 10));
    }

    for (const chunk of chunks) {
      const rolesQuery = query(
        collection(db, 'auth_roles'),
        where('__name__', 'in', chunk),
        where('status', '==', 'active')
      );
      const rolesSnapshot = await getDocs(rolesQuery);

      rolesSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const permissions = data.permissions as string[] | undefined;
        if (permissions && Array.isArray(permissions)) {
          allPermissions.push(...permissions);
        }
      });
    }
  } catch (err) {
    console.error('[V167] auth_roles 조회 실패:', err);
  }

  // 중복 제거 및 타입 캐스팅 (DB에서 가져온 권한을 PermissionKey로 신뢰)
  return Array.from(new Set(allPermissions)) as PermissionKey[];
}
